import { createMemo, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { RepoEnablementRow } from '../lib/repo-enablement';
import type { RepoEnablementPicker } from '../lib/repo-enablement-picker';
import { ago } from '../lib/time';
import { DataTable, type DataTableColumn } from './DataTable';
import { StatusPill } from './StatusPill';

// The picker's rows. Presenter. The select column waits on `/v1/me`, so a reader who may not act
// sees a list with nothing to press, and the memo keeps the column objects stable otherwise.

export function RepoEnablementTable(props: {
  readonly picker: RepoEnablementPicker;
  readonly canAct: boolean;
  readonly rows: readonly RepoEnablementRow[];
}): JSX.Element {
  const columns = createMemo((): readonly DataTableColumn<RepoEnablementRow>[] => [
    ...(props.canAct
      ? [
          {
            key: 'select',
            header: t('repo_enablement.column_select'),
            srOnlyHeader: true,
            cell: (row: RepoEnablementRow) => (
              <input
                type="checkbox"
                checked={props.picker.allMatching() || props.picker.picked().has(row.id)}
                disabled={props.picker.allMatching()}
                aria-label={t('repo_enablement.select_row', { name: row.full_name })}
                onChange={() => props.picker.togglePick(row.id)}
              />
            ),
          },
        ]
      : []),
    {
      key: 'name',
      header: t('repo_enablement.column_name'),
      rowHeader: true,
      mono: true,
      cell: (row) => row.full_name,
    },
    {
      key: 'language',
      header: t('repo_enablement.column_language'),
      class: 'repo-enablement__optional',
      cell: (row) => row.language ?? t('repo_enablement.unknown'),
    },
    {
      key: 'pushed',
      header: t('repo_enablement.column_pushed'),
      class: 'repo-enablement__optional',
      cell: (row) => (row.pushed_at === null ? t('repo_enablement.unknown') : ago(row.pushed_at)),
    },
    {
      key: 'state',
      header: t('repo_enablement.column_state'),
      cell: (row) => (
        <span class="repo-enablement__state">
          <StatusPill tone={row.enabled ? 'success' : 'neutral'}>
            {row.enabled ? t('repo_enablement.state_on') : t('repo_enablement.state_off')}
          </StatusPill>
          <Show when={row.private}>
            <StatusPill tone="neutral">{t('repo_enablement.visibility_private')}</StatusPill>
          </Show>
          <Show when={row.archived}>
            <StatusPill tone="neutral">{t('repo_enablement.archived')}</StatusPill>
          </Show>
        </span>
      ),
    },
  ]);
  return (
    <DataTable
      label={t('repo_enablement.table_label')}
      columns={columns()}
      rows={props.rows}
      rowKey={(row) => row.id}
    />
  );
}
